// Keep Fit App - 飲食記錄頁面

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getRecords, deleteRecord } from '../services/api';
import type { FoodRecord } from '../types';
import { MEAL_TYPE_LABELS } from '../types';

export default function Records() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // 獲取記錄
  const { data, isLoading, error } = useQuery({
    queryKey: ['records', selectedDate],
    queryFn: () => getRecords({ date: selectedDate }),
  });

  // 刪除突變
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRecord(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['records'] });
    },
  });

  const records = data?.data || [];

  // 按餐類分組
  const groupedRecords = records.reduce((acc, record) => {
    const type = record.mealType;
    if (!acc[type]) acc[type] = [];
    acc[type].push(record);
    return acc;
  }, {} as Record<string, FoodRecord[]>);

  const mealOrder = ['breakfast', 'lunch', 'dinner', 'snack'];

  // 計算總計
  const totalCalories = records.reduce((sum, r) => sum + (r.calories || 0), 0);
  const totalProtein = records.reduce((sum, r) => sum + (r.protein || 0), 0);
  const totalFat = records.reduce((sum, r) => sum + (r.fat || 0), 0);
  const totalCarbs = records.reduce((sum, r) => sum + (r.carbs || 0), 0);

  // 日期導航
  const changeDate = (days: number) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dateStr === today.toISOString().split('T')[0]) return '今日';
    if (dateStr === yesterday.toISOString().split('T')[0]) return '昨日';
    
    return date.toLocaleDateString('zh-TW', { 
      month: 'short', 
      day: 'numeric',
      weekday: 'short'
    });
  };

  const handleDelete = (id: string, name: string) => {
    if (confirm(`確定要刪除「${name}」嗎？`)) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-800">📝 飲食記錄</h1>

      {/* 日期選擇 */}
      <div className="flex items-center justify-between bg-white rounded-xl p-3 shadow-sm">
        <button
          onClick={() => changeDate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          ◀
        </button>
        <div className="text-center">
          <div className="font-medium">{formatDate(selectedDate)}</div>
          <div className="text-xs text-gray-500">{selectedDate}</div>
        </div>
        <button
          onClick={() => changeDate(1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
          disabled={selectedDate === new Date().toISOString().split('T')[0]}
        >
          ▶
        </button>
      </div>

      {/* 總計 */}
      {records.length > 0 && (
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-medium mb-3">📊 營養總計</h3>
          <div className="grid grid-cols-4 gap-2 text-center text-sm">
            <div>
              <div className="font-bold text-orange-600">{totalCalories}</div>
              <div className="text-xs text-gray-500">卡路里</div>
            </div>
            <div>
              <div className="font-bold text-red-600">{Math.round(totalProtein)}g</div>
              <div className="text-xs text-gray-500">蛋白質</div>
            </div>
            <div>
              <div className="font-bold text-yellow-600">{Math.round(totalFat)}g</div>
              <div className="text-xs text-gray-500">脂肪</div>
            </div>
            <div>
              <div className="font-bold text-blue-600">{Math.round(totalCarbs)}g</div>
              <div className="text-xs text-gray-500">碳水</div>
            </div>
          </div>
        </div>
      )}

      {/* 記錄列表 */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-400">載入中...</div>
      ) : error ? (
        <div className="text-center py-8 text-red-500">載入失敗</div>
      ) : records.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <div className="text-4xl mb-2">🈳</div>
          <p>當日沒有任何記錄</p>
        </div>
      ) : (
        <div className="space-y-4">
          {mealOrder.map((mealType) => {
            const mealRecords = groupedRecords[mealType];
            if (!mealRecords || mealRecords.length === 0) return null;

            return (
              <div key={mealType} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b">
                  <span className="font-medium">
                    {mealType === 'breakfast' && '🌅'}
                    {mealType === 'lunch' && '☀️'}
                    {mealType === 'dinner' && '🌙'}
                    {mealType === 'snack' && '🍪'}
                    {' '}{MEAL_TYPE_LABELS[mealType]}
                  </span>
                  <span className="text-sm text-gray-500 ml-2">
                    ({mealRecords.length} 項)
                  </span>
                </div>
                
                <div className="divide-y">
                  {mealRecords.map((record) => (
                    <div
                      key={record.id}
                      className="p-3 flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{record.foodName}</div>
                        <div className="text-sm text-gray-500">
                          {record.calories} kcal
                          <span className="mx-1">|</span>
                          P:{record.protein}g
                          <span className="mx-1">|</span>
                          F:{record.fat}g
                          <span className="mx-1">|</span>
                          C:{record.carbs}g
                        </div>
                        {record.notes && (
                          <div className="text-xs text-gray-400 mt-1">
                            📝 {record.notes}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleDelete(record.id, record.foodName)}
                        className="text-red-400 hover:text-red-600 p-2"
                        disabled={deleteMutation.isPending}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
